import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { FormControl, Select, MenuItem, Chip, Button, CircularProgress } from '@mui/material';
import Apexcharts from 'react-apexcharts';
import { getPipelines } from '../../../store/Deals';
import { getProperties, getObjects } from '../../../store/Objects';
import { buildMongoQuery } from '../../../utils/filterConfig';
import AdvancedFilterModal from '../../../components/Filters/AdvancedFilter';
import QuickFilters from '../../../components/Filters/QuickFilters';
import { dealsQuickFilterConfig, contactsQuickFilterConfig, companiesQuickFilterConfig, activitiesQuickFilterConfig, agentsQuickFilterConfig } from '../../../utils/quickFiltersConfig';
import constants from '../../../constants';

// Dashboard-specific QuickFilter configs (without pipeline dropdown for deals)
const dashboardDealsQuickFilterConfig = {
  ...dealsQuickFilterConfig,
  dropdowns: [] // Remove pipeline dropdown - use the main Pipeline selector instead
};

const chartTypes = [
  { value: 'column', label: 'Column', icon: 'column' },
  { value: 'bar', label: 'Bar', icon: 'bar' },
  { value: 'donut', label: 'Donut', icon: 'donut' },
  { value: 'line', label: 'Line', icon: 'line' },
  { value: 'number', label: 'Number', icon: 'number' },
  { value: 'table', label: 'Table', icon: 'table' }
];

// Metric Categories for dropdown with icons (using same icons as sidebar navigation)
const metricCategories = [
  {
    value: 'agents',
    label: 'Agents',
    icon: <svg width="18" height="16" viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.0784 14.7194C19.9785 15.789 19.641 16.7644 19.0031 17.6185C17.8657 19.1404 16.3431 19.9588 14.4394 19.9824C12.2146 20.0095 9.98909 20.0009 7.7636 19.9859C4.84741 19.9667 2.43497 17.754 2.13101 14.8471C2.12673 14.8043 2.1146 14.7622 2.10746 14.7244C0.792433 14.4632 0.069629 13.7683 0.00469798 12.7008C-0.033119 12.0851 0.157393 11.5442 0.57552 11.0875C0.986512 10.6387 1.51381 10.4325 2.11317 10.3854C2.20522 9.3501 2.51917 8.41252 3.10997 7.58412C4.26374 5.96726 5.84635 5.14029 7.83138 5.10318C8.66764 5.08748 9.5046 5.10033 10.3416 5.10033C10.4386 5.10033 10.5357 5.10033 10.6719 5.10033C10.6719 4.81777 10.6719 4.55377 10.6719 4.28976C10.6719 3.85379 10.6648 3.41712 10.6755 2.98115C10.6798 2.81918 10.6355 2.73641 10.4793 2.66006C9.86636 2.36181 9.56882 1.70108 9.73079 1.05177C9.88634 0.429575 10.4536 -0.00424976 11.1065 3.14058e-05C11.7679 0.00431257 12.3152 0.447413 12.4679 1.10172C12.612 1.71963 12.3045 2.35253 11.7165 2.66648C11.6416 2.70644 11.5824 2.78279 11.531 2.85343C11.5096 2.88268 11.5267 2.93977 11.5267 2.98472C11.5267 3.67755 11.5267 4.36968 11.5267 5.09961C11.6509 5.09961 11.7544 5.0989 11.8571 5.09961C12.8803 5.11103 13.9064 5.07749 14.926 5.14243C17.4697 5.30582 19.7166 7.46853 20.0291 10.0015C20.0441 10.1243 20.0641 10.247 20.0812 10.3633C21.5818 10.7729 22.2411 11.4878 22.1826 12.6323C22.1269 13.7661 21.382 14.5125 20.0784 14.7194ZM11.0929 19.1347C12.1696 19.1347 13.2463 19.1404 14.3231 19.1333C17.1001 19.1154 19.22 16.9948 19.2328 14.2242C19.2378 13.1033 19.24 11.9816 19.2293 10.8606C19.2264 10.5331 19.205 10.1999 19.1387 9.88025C18.6563 7.54131 16.707 5.96441 14.2888 5.95513C12.251 5.94729 10.2131 5.95228 8.1753 5.95513C7.88204 5.95585 7.58807 5.96798 7.29624 5.99081C5.13568 6.16277 3.1592 8.05362 3.00936 10.2163C2.90947 11.6512 2.97083 13.0968 2.96512 14.5382C2.96441 14.7058 2.98796 14.8757 3.01864 15.0419C3.46388 17.49 5.42109 19.1261 7.91629 19.134C8.97445 19.1383 10.0333 19.1347 11.0929 19.1347ZM20.1091 11.2909C20.1091 12.1357 20.1091 12.9812 20.1091 13.8275C20.8604 13.7775 21.3884 13.1696 21.3278 12.4447C21.2728 11.7775 20.7056 11.2381 20.1091 11.2909ZM2.07036 13.8303C2.07036 12.967 2.07036 12.1214 2.07036 11.2752C1.39679 11.3016 0.872348 11.8446 0.855223 12.5103C0.838098 13.2046 1.34399 13.7618 2.07036 13.8303ZM11.0908 0.856978C10.7897 0.859119 10.5528 1.09387 10.5492 1.39426C10.5456 1.70536 10.7904 1.94939 11.1029 1.94582C11.4054 1.94297 11.6395 1.70964 11.6438 1.40854C11.6473 1.09744 11.4047 0.854838 11.0908 0.856978Z" fill="currentColor"/><path d="M11.0858 16.6408C9.91135 16.6408 8.73688 16.6429 7.56241 16.6401C5.78644 16.6365 4.59913 15.4499 4.59699 13.6782C4.59628 12.904 4.59414 12.1298 4.59771 11.3557C4.60484 9.65747 5.81498 8.45161 7.51389 8.4509C9.8985 8.44947 12.2831 8.44947 14.6684 8.4509C16.3773 8.45161 17.5753 9.63678 17.5868 11.3464C17.5925 12.2362 17.6046 13.1266 17.5739 14.0157C17.5268 15.3999 16.3638 16.5837 14.9845 16.6222C13.6866 16.6586 12.3866 16.6301 11.0872 16.6301C11.0858 16.6336 11.0858 16.6372 11.0858 16.6408ZM11.0801 15.786C12.2902 15.786 13.4997 15.7917 14.7091 15.7845C15.9071 15.7767 16.7184 14.969 16.7298 13.776C16.7377 12.9575 16.737 12.1391 16.7298 11.3207C16.7191 10.117 15.8936 9.30642 14.6856 9.30571C12.2924 9.30428 9.89993 9.30428 7.50676 9.30571C6.2802 9.30642 5.45537 10.1363 5.4518 11.3707C5.44966 12.1356 5.45109 12.9005 5.4518 13.6654C5.45251 14.9754 6.25309 15.7824 7.55813 15.7845C8.73189 15.7874 9.90564 15.786 11.0801 15.786Z" fill="currentColor"/><path d="M8.341 14.3347C7.35419 14.3305 6.5636 13.5363 6.5636 12.5488C6.5636 11.552 7.3706 10.7457 8.36312 10.7521C9.34208 10.7585 10.1441 11.5648 10.1455 12.5438C10.1462 13.5384 9.33994 14.3383 8.341 14.3347ZM8.31745 13.4785C8.8476 13.4928 9.28214 13.0832 9.28999 12.5609C9.29784 12.0507 8.8847 11.6212 8.37382 11.6076C7.86507 11.5941 7.43054 12.0093 7.41912 12.5188C7.4077 13.0411 7.80371 13.4642 8.31745 13.4785Z" fill="currentColor"/><path d="M13.846 14.3347C12.8464 14.3383 12.0465 13.5384 12.0508 12.5402C12.0551 11.5555 12.8521 10.7557 13.8325 10.7521C14.8193 10.7485 15.6406 11.5691 15.6348 12.553C15.6299 13.5356 14.8307 14.3311 13.846 14.3347ZM13.8418 13.4785C14.3562 13.477 14.7765 13.0611 14.7786 12.5502C14.7808 12.035 14.3384 11.5976 13.8225 11.6076C13.3109 11.6169 12.902 12.0407 12.9063 12.5559C12.9113 13.076 13.3209 13.4799 13.8418 13.4785Z" fill="currentColor"/></svg>
  },
  {
    value: 'deals',
    label: 'Deals',
    icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.47002 17.9987C7.99536 17.9225 7.6343 17.6574 7.3089 17.3219C7.05176 17.0568 6.77436 16.8116 6.51413 16.5496C6.34153 16.3761 6.14602 16.3211 5.90605 16.3598C5.46045 16.4316 5.01308 16.4963 4.56528 16.5518C3.575 16.6746 2.85861 16.1264 2.69525 15.1322C2.62524 14.7051 2.54642 14.2789 2.47553 13.8518C2.43722 13.6202 2.31701 13.4599 2.10566 13.3524C1.69837 13.1455 1.29503 12.9302 0.893024 12.7127C0.056859 12.2609 -0.223184 11.4023 0.185872 10.545C0.382254 10.1328 0.582159 9.72245 0.789109 9.3156C0.899188 9.09896 0.898308 8.89553 0.788228 8.67934C0.58392 8.27777 0.387098 7.87223 0.192037 7.46626C-0.227147 6.59443 0.0559784 5.73273 0.911077 5.2748C1.30384 5.06432 1.6944 4.85033 2.09289 4.65174C2.32538 4.5355 2.4425 4.35981 2.48257 4.10839C2.55391 3.65751 2.6292 3.20706 2.71991 2.76014C2.91365 1.81037 3.66395 1.30136 4.62252 1.45195C5.06856 1.52196 5.51593 1.58184 5.96285 1.64613C6.16892 1.67607 6.34196 1.61487 6.49167 1.46868C6.82676 1.14065 7.16448 0.814809 7.50397 0.491175C8.19086 -0.163578 9.101 -0.163578 9.78834 0.490295C10.1195 0.805563 10.4515 1.12083 10.7747 1.44446C10.953 1.62279 11.1529 1.67739 11.399 1.63732C11.8446 1.56467 12.2915 1.4973 12.7398 1.44579C13.716 1.33394 14.4143 1.8588 14.5905 2.82751C14.6679 3.25373 14.7446 3.67996 14.8106 4.10751C14.8494 4.35937 14.9669 4.5355 15.1994 4.65174C15.5922 4.84813 15.9775 5.0608 16.3654 5.26731C17.2487 5.73757 17.5269 6.59707 17.0879 7.49752C16.9008 7.88148 16.7198 8.26896 16.5217 8.64719C16.3962 8.88673 16.3936 9.10645 16.519 9.34686C16.7247 9.74094 16.9158 10.143 17.1069 10.5445C17.5164 11.4036 17.2381 12.26 16.4019 12.7131C16.0052 12.928 15.6085 13.1433 15.2047 13.3445C14.9652 13.4638 14.8489 13.6448 14.8093 13.9033C14.7406 14.3484 14.6644 14.7932 14.5746 15.2344C14.3809 16.1868 13.635 16.6962 12.6759 16.5478C12.2299 16.4787 11.7821 16.4188 11.3352 16.3541C11.1295 16.3241 10.9547 16.3801 10.805 16.5271C10.5329 16.794 10.2498 17.0498 9.98428 17.3232C9.65844 17.6583 9.29738 17.9238 8.82316 18C8.70515 17.9987 8.58759 17.9987 8.47002 17.9987ZM12.9604 15.536C13.2704 15.5457 13.4875 15.3819 13.5416 15.1005C13.6187 14.6985 13.705 14.2965 13.7525 13.8905C13.8371 13.164 14.1902 12.6519 14.8621 12.3463C15.2188 12.1843 15.5587 11.9848 15.9052 11.7999C16.2544 11.6132 16.3425 11.3481 16.1712 10.995C15.9722 10.5842 15.7745 10.1729 15.568 9.7656C15.3086 9.25307 15.3082 8.74583 15.5671 8.2333C15.7674 7.83657 15.9594 7.435 16.154 7.03519C16.3473 6.63802 16.2663 6.38968 15.8749 6.18229C15.4711 5.9683 15.0664 5.75562 14.6631 5.54075C14.2008 5.29417 13.9066 4.92078 13.812 4.4012C13.7226 3.91201 13.6394 3.42149 13.5473 2.93318C13.4826 2.58973 13.244 2.41977 12.9014 2.46732C12.454 2.52941 12.0067 2.59061 11.5611 2.66195C10.968 2.75705 10.4616 2.60382 10.0354 2.17583C9.71657 1.85616 9.38809 1.54618 9.06181 1.23399C8.78529 0.969361 8.50437 0.970682 8.22565 1.2384C7.88704 1.56335 7.55108 1.89051 7.21291 2.2159C6.84085 2.57388 6.40097 2.74296 5.88271 2.67515C5.3944 2.61131 4.90697 2.54174 4.41953 2.47085C4.05363 2.41757 3.8119 2.57917 3.74365 2.93935C3.67055 3.32462 3.58469 3.70858 3.54154 4.09738C3.46008 4.83272 3.10078 5.34701 2.42313 5.65479C2.07704 5.81198 1.74768 6.00616 1.41128 6.18493C1.02908 6.38792 0.945862 6.63934 1.13344 7.02462C1.3307 7.42971 1.52576 7.83569 1.72787 8.23858C1.98237 8.74539 1.98325 9.24823 1.72919 9.75548C1.52752 10.1584 1.33158 10.5643 1.13432 10.9694C0.945862 11.356 1.0282 11.6083 1.4082 11.8104C1.80668 12.0222 2.20385 12.2358 2.60454 12.4432C3.12236 12.7113 3.41341 13.134 3.50367 13.7069C3.57368 14.1516 3.65514 14.595 3.73924 15.0375C3.81278 15.4255 4.04703 15.5818 4.43363 15.5236C4.88011 15.4563 5.32747 15.3964 5.77395 15.3303C6.35562 15.2445 6.84745 15.4158 7.26311 15.8283C7.57089 16.1339 7.8866 16.432 8.19879 16.7332C8.51714 17.0401 8.77649 17.0383 9.09836 16.7279C9.40218 16.4347 9.71304 16.1485 10.0081 15.8468C10.455 15.3898 10.9838 15.233 11.6113 15.3436C12.0674 15.4237 12.5284 15.4752 12.9604 15.536Z" fill="currentColor"/><path d="M12.6175 5.59131C12.5734 5.69082 12.536 5.79342 12.4836 5.88809C12.4532 5.94268 12.3995 5.98496 12.3537 6.03031C10.1261 8.25436 7.89858 10.4788 5.66704 12.6989C5.56753 12.798 5.43676 12.8865 5.30422 12.9266C5.09595 12.9895 4.87579 12.8711 4.76659 12.684C4.65651 12.4946 4.67457 12.2674 4.81635 12.0939C4.85334 12.0486 4.89561 12.0076 4.937 11.9662C7.16501 9.74219 9.39346 7.51903 11.6206 5.29454C11.7478 5.16772 11.8777 5.05632 12.0724 5.05676C12.3665 5.05764 12.5972 5.28705 12.6175 5.59131Z" fill="currentColor"/><path d="M6.56915 8.12983C5.51899 8.13291 4.66653 7.28662 4.66213 6.23778C4.65772 5.19378 5.50886 4.3334 6.55329 4.32592C7.60125 4.31843 8.47088 5.18189 8.47088 6.23029C8.47088 7.27649 7.61931 8.12718 6.56915 8.12983ZM6.56606 5.37079C6.0892 5.37079 5.70436 5.75035 5.69952 6.22545C5.69467 6.69747 6.10065 7.10168 6.57355 7.09596C7.04425 7.08979 7.43305 6.69527 7.42953 6.22677C7.42645 5.75079 7.04337 5.37079 6.56606 5.37079Z" fill="currentColor"/><path d="M12.629 11.7735C12.6273 12.8162 11.7678 13.6721 10.7242 13.6708C9.68111 13.6695 8.82117 12.8104 8.82117 11.77C8.82117 10.7141 9.67891 9.86206 10.7366 9.86823C11.7898 9.87483 12.6308 10.7211 12.629 11.7735ZM10.7273 12.6264C11.2051 12.6255 11.589 12.2451 11.5917 11.77C11.5943 11.3023 11.2046 10.9087 10.733 10.9025C10.2579 10.8964 9.85636 11.2979 9.86164 11.773C9.86692 12.2499 10.2496 12.6273 10.7273 12.6264Z" fill="currentColor"/></svg>
  },
  {
    value: 'contacts',
    label: 'Contacts',
    icon: <svg width="14" height="16" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.43101 3.06672C1.43101 2.74001 1.42586 2.44079 1.43204 2.14226C1.453 1.08245 2.16 0.236664 3.19301 0.038444C3.3452 0.00924345 3.50357 0.00202918 3.65919 0.00202918C6.97741 0.000311503 10.2956 -0.00140618 13.6142 0.00202918C14.7533 0.00305979 15.6173 0.711088 15.8077 1.78636C15.8314 1.92068 15.8365 2.06016 15.8365 2.19688C15.8379 6.73362 15.8403 11.2707 15.8355 15.8074C15.8345 16.7965 15.2824 17.5801 14.3775 17.8779C14.1333 17.9583 13.8632 17.993 13.6049 17.9937C10.2929 18.0013 6.98084 18.0013 3.66881 17.9978C2.33692 17.9965 1.42895 17.0748 1.42758 15.7384C1.42723 15.4773 1.42758 15.2166 1.42758 14.9338C1.13763 14.9338 0.864865 14.9349 0.592097 14.9331C0.511023 14.9328 0.428574 14.9338 0.349904 14.9184C0.121453 14.8734 -0.0197408 14.681 0.00224555 14.4567C0.0228577 14.2437 0.192221 14.0832 0.419299 14.0777C0.700311 14.0712 0.981667 14.0757 1.26302 14.0753C1.31146 14.0753 1.35956 14.0753 1.41796 14.0753C1.41796 13.1344 1.41796 12.2123 1.41796 11.2631C1.18332 11.2631 0.948344 11.2631 0.713709 11.2631C0.626107 11.2631 0.538506 11.2656 0.451247 11.2625C0.183633 11.2528 0.00155848 11.0787 0.000871407 10.8348C0.000184335 10.5902 0.181915 10.4098 0.448156 10.4053C0.765927 10.3998 1.08404 10.404 1.41521 10.404C1.41521 9.46645 1.41521 8.53994 1.41521 7.59281C1.10706 7.59281 0.802685 7.59384 0.498312 7.59246C0.187755 7.59075 -0.00290749 7.42276 0.00121494 7.1572C0.00499384 6.9023 0.195313 6.73706 0.494877 6.73431C0.79925 6.73156 1.10397 6.73362 1.4183 6.73362C1.4183 5.79302 1.4183 4.87097 1.4183 3.92143C1.13214 3.92143 0.847345 3.92212 0.56221 3.92109C0.481135 3.92075 0.397656 3.92521 0.319673 3.90872C0.126949 3.8675 -0.00668639 3.67924 0.00155848 3.47759C0.0101469 3.26356 0.167143 3.08905 0.395251 3.07496C0.644315 3.0595 0.894753 3.06878 1.14485 3.0674C1.23073 3.06637 1.31765 3.06672 1.43101 3.06672ZM2.28951 14.9335C2.28951 15.2245 2.28917 15.4862 2.28951 15.748C2.29054 16.6017 2.82852 17.1428 3.68118 17.1431C6.98702 17.1438 10.2925 17.1438 13.5984 17.1431C14.4493 17.1428 14.99 16.6007 14.9904 15.746C14.9914 11.2467 14.9914 6.74736 14.9887 2.24841C14.9887 2.10001 14.9739 1.94713 14.9354 1.80422C14.7767 1.21231 14.2782 0.855717 13.6135 0.855373C10.2953 0.853999 6.97706 0.854686 3.65885 0.855373C3.57777 0.855373 3.49635 0.862244 3.41597 0.871863C2.90478 0.934387 2.44651 1.28995 2.35135 1.79151C2.27336 2.20169 2.29226 2.63008 2.26718 3.06672C2.6193 3.06672 2.92471 3.06775 3.23011 3.06637C3.41047 3.06569 3.56369 3.11928 3.66056 3.28108C3.83027 3.56484 3.63102 3.91147 3.28851 3.91903C2.96009 3.92624 2.63133 3.92075 2.29947 3.92075C2.29947 4.86994 2.29947 5.79199 2.29947 6.72538C2.35032 6.72847 2.39257 6.73294 2.43448 6.73328C2.72202 6.73431 3.00956 6.72641 3.29676 6.7374C3.55166 6.74736 3.72103 6.93356 3.7169 7.17301C3.71278 7.41279 3.53723 7.58216 3.27993 7.59143C3.18614 7.59487 3.09235 7.59246 2.99891 7.59246C2.76462 7.59246 2.53033 7.59246 2.30188 7.59246C2.30188 8.54578 2.30188 9.46783 2.30188 10.4036C2.63133 10.4036 2.94876 10.3995 3.26653 10.405C3.5575 10.4101 3.74439 10.6152 3.71519 10.8856C3.69114 11.1096 3.51353 11.259 3.25553 11.2621C2.93914 11.2659 2.6224 11.2631 2.30497 11.2631C2.30497 12.212 2.30497 13.1385 2.30497 14.0764C2.6437 14.0764 2.96799 14.0705 3.29229 14.0781C3.60216 14.085 3.80004 14.3742 3.69114 14.6514C3.60903 14.86 3.44001 14.9338 3.22908 14.9338C2.92265 14.9338 2.61759 14.9335 2.28951 14.9335Z" fill="currentColor"/><path d="M8.63305 13.2859C7.75188 13.2859 6.87105 13.2866 5.98988 13.2856C5.61199 13.2852 5.44984 13.1189 5.46152 12.7479C5.50996 11.2127 6.5021 9.97181 7.9446 9.64202C9.75126 9.22908 11.5466 10.4871 11.7757 12.3274C11.7943 12.4758 11.8094 12.6256 11.8125 12.7751C11.8197 13.1148 11.6531 13.2845 11.3137 13.2852C10.4201 13.2873 9.52659 13.2859 8.63305 13.2859ZM10.9079 12.4192C10.8399 11.3363 9.80863 10.4308 8.64645 10.4229C7.48667 10.4153 6.46362 11.3064 6.36503 12.4192C7.87487 12.4192 9.38368 12.4192 10.9079 12.4192Z" fill="currentColor"/><path d="M8.64027 8.87037C7.49389 8.87175 6.56359 7.94695 6.56153 6.80366C6.55912 5.64938 7.49629 4.71015 8.64336 4.71702C9.78699 4.72389 10.718 5.65796 10.7173 6.79747C10.7163 7.94042 9.78562 8.869 8.64027 8.87037ZM8.63958 8.00913C9.31051 8.0081 9.85535 7.46737 9.85948 6.7985C9.8636 6.12552 9.30844 5.56864 8.63477 5.56967C7.95938 5.57071 7.4159 6.12311 7.42003 6.804C7.42415 7.4763 7.96419 8.01016 8.63958 8.00913Z" fill="currentColor"/></svg>
  },
  {
    value: 'companies',
    label: 'Companies',
    icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.9999 17.0625H5.99993C2.53493 17.0625 1.88993 15.45 1.72493 13.8825L1.16243 7.875C1.07993 7.0875 1.05743 5.925 1.83743 5.055C2.51243 4.305 3.62993 3.945 5.24993 3.945H12.7499C14.3774 3.945 15.4949 4.3125 16.1624 5.055C16.9424 5.925 16.9199 7.0875 16.8374 7.8825L16.2749 13.875C16.1099 15.45 15.4649 17.0625 11.9999 17.0625ZM5.24993 5.0625C3.98243 5.0625 3.11243 5.31 2.66993 5.805C2.30243 6.21 2.18243 6.8325 2.27993 7.7625L2.84243 13.77C2.96993 14.955 3.29243 15.9375 5.99993 15.9375H11.9999C14.6999 15.9375 15.0299 14.955 15.1574 13.7625L15.7199 7.77C15.8174 6.8325 15.6974 6.21 15.3299 5.805C14.8874 5.31 14.0174 5.0625 12.7499 5.0625H5.24993Z" fill="currentColor"/><path d="M12 5.0625C11.6925 5.0625 11.4375 4.8075 11.4375 4.5V3.9C11.4375 2.565 11.4375 2.0625 9.6 2.0625H8.4C6.5625 2.0625 6.5625 2.565 6.5625 3.9V4.5C6.5625 4.8075 6.3075 5.0625 6 5.0625C5.6925 5.0625 5.4375 4.8075 5.4375 4.5V3.9C5.4375 2.58 5.4375 0.9375 8.4 0.9375H9.6C12.5625 0.9375 12.5625 2.58 12.5625 3.9V4.5C12.5625 4.8075 12.3075 5.0625 12 5.0625Z" fill="currentColor"/><path d="M9 12.5625C6.9375 12.5625 6.9375 11.2875 6.9375 10.5225V9.75C6.9375 8.6925 7.1925 8.4375 8.25 8.4375H9.75C10.8075 8.4375 11.0625 8.6925 11.0625 9.75V10.5C11.0625 11.28 11.0625 12.5625 9 12.5625ZM8.0625 9.5625C8.0625 9.6225 8.0625 9.69 8.0625 9.75V10.5225C8.0625 11.295 8.0625 11.4375 9 11.4375C9.9375 11.4375 9.9375 11.3175 9.9375 10.515V9.75C9.9375 9.69 9.9375 9.6225 9.9375 9.5625C9.8775 9.5625 9.81 9.5625 9.75 9.5625H8.25C8.19 9.5625 8.1225 9.5625 8.0625 9.5625Z" fill="currentColor"/><path d="M10.5001 11.0775C10.2226 11.0775 9.97512 10.8675 9.94512 10.5825C9.90762 10.275 10.1251 9.99 10.4326 9.9525C12.4126 9.705 14.3101 8.955 15.9076 7.7925C16.1551 7.605 16.5076 7.665 16.6951 7.92C16.8751 8.1675 16.8226 8.52 16.5676 8.7075C14.8126 9.9825 12.7426 10.8 10.5676 11.0775C10.5451 11.0775 10.5226 11.0775 10.5001 11.0775Z" fill="currentColor"/><path d="M7.50005 11.085C7.47755 11.085 7.45505 11.085 7.43255 11.085C5.37755 10.8525 3.37505 10.1025 1.64255 8.9175C1.38755 8.745 1.32005 8.3925 1.49255 8.1375C1.66505 7.8825 2.01755 7.815 2.27255 7.9875C3.85505 9.0675 5.67755 9.75 7.55255 9.9675C7.86005 10.005 8.08505 10.2825 8.04755 10.59C8.02505 10.875 7.78505 11.085 7.50005 11.085Z" fill="currentColor"/></svg>
  },
  {
    value: 'activities',
    label: 'Activities',
    icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.9949 14.557C16.9949 15.9265 15.8868 17.0345 14.5173 17.0346C13.1478 17.0346 12.0398 15.9266 12.0398 14.557C12.0398 13.1875 13.1478 12.0795 14.5173 12.0795C15.8868 12.0795 16.9948 13.1876 16.9949 14.557ZM15.7439 14.557C15.7439 13.8798 15.1946 13.3305 14.5173 13.3305C13.8401 13.3305 13.2908 13.8798 13.2908 14.557C13.2908 15.2343 13.84 15.7836 14.5173 15.7836C15.1946 15.7836 15.7439 15.2343 15.7439 14.557Z" fill="currentColor" stroke="white" strokeWidth="0.138462"/><path d="M5.95825 14.557C5.95825 15.9265 4.85021 17.0345 3.48071 17.0346C2.11118 17.0346 1.00317 15.9266 1.00317 14.557C1.00322 13.1875 2.1112 12.0795 3.48071 12.0795C4.85019 12.0795 5.95821 13.1876 5.95825 14.557ZM4.70728 14.557C4.70723 13.8798 4.15794 13.3305 3.48071 13.3305C2.80345 13.3305 2.25419 13.8798 2.25415 14.557C2.25415 15.2343 2.80342 15.7836 3.48071 15.7836C4.15797 15.7836 4.70728 15.2343 4.70728 14.557Z" fill="currentColor" stroke="white" strokeWidth="0.138462"/><path d="M11.4771 3.48029C11.4771 4.8498 10.369 5.95778 8.99951 5.95782C7.62998 5.95782 6.52197 4.84982 6.52197 3.48029C6.52202 2.11079 7.63 1.00275 8.99951 1.00275C10.369 1.00279 11.477 2.11081 11.4771 3.48029ZM10.2261 3.48029C10.226 2.80306 9.67674 2.25377 8.99951 2.25372C8.32225 2.25372 7.77299 2.80303 7.77295 3.48029C7.77295 4.15758 8.32222 4.70685 8.99951 4.70685C9.67676 4.7068 10.2261 4.15755 10.2261 3.48029Z" fill="currentColor" stroke="white" strokeWidth="0.138462"/><path d="M8.4819 8.61792V5.29489C8.4819 5.18136 8.71643 5.0872 8.99925 5.0872C9.28207 5.0872 9.5166 5.18136 9.5166 5.29489V8.61792C9.5166 8.73145 9.28207 8.82561 8.99925 8.82561C8.71643 8.82561 8.4819 8.73145 8.4819 8.61792Z" fill="currentColor" stroke="currentColor" strokeWidth="0.138462"/><path d="M2.96414 12.1851V11.3128C2.96414 9.64351 3.88157 8.72607 5.55089 8.72607L12.4489 8.72607C14.1182 8.72607 15.0356 9.64351 15.0356 11.3128L15.0356 12.1851C15.0356 12.4679 14.8011 12.7025 14.5183 12.7025C14.2355 12.7025 14.0009 12.4679 14.0009 12.1851V11.3128C14.0009 10.2229 13.5388 9.76077 12.4489 9.76077L5.55089 9.76077C4.461 9.76077 3.99884 10.2229 3.99884 11.3128V12.1851C3.99884 12.4679 3.76431 12.7025 3.48149 12.7025C3.19867 12.7025 2.96414 12.4679 2.96414 12.1851Z" fill="currentColor" stroke="currentColor" strokeWidth="0.138462"/></svg>
  }
];

const dateRangeOptions = [
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_4_weeks', label: 'Last 4 weeks' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'week_to_date', label: 'Week to date' },
  { value: 'month_to_date', label: 'Month to date' },
  { value: 'year_to_date', label: 'Year to date' },
  { value: 'all_time', label: 'All time' }
];

const sizeOptions = [ { value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' } ];
const viewByOptions = [ { value: 'hour', label: 'Hour' }, { value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' } ];

// Grid size options for 12-column system
const gridColOptions = [
  { value: 3, label: '3 cols (1/4)' },
  { value: 4, label: '4 cols (1/3)' },
  { value: 6, label: '6 cols (1/2)' },
  { value: 8, label: '8 cols (2/3)' },
  { value: 12, label: '12 cols (Full)' }
];

const gridRowOptions = [
  { value: 1, label: '1 row' },
  { value: 2, label: '2 rows' },
  { value: 3, label: '3 rows' },
  { value: 4, label: '4 rows' }
];

const defaultWidgetConfig = {
  type: 'column',
  title: '',
  metricCategory: 'agents',
  // Agent fields
  agentId: 'all',
  // CRM fields
  ownerId: 'all',
  pipelineId: 'all',
  // Common fields
  dateRange: 'all_time',
  size: 'small',
  viewBy: 'day',
  // Grid sizing (1-12 columns, 1-4 rows)
  gridCols: 4,
  gridRows: 2,
  // Number card color (null = random from chartColors)
  numberColor: null
};

const ChartModal = ({ isOpen, onClose, mode, widgetData, onSave }) => {
  const dispatch = useDispatch();
  const agents = useSelector(state => state.Agent?.allAgents || []);
  const pipelines = useSelector(state => state.Deals?.pipelines || []);
  const workspaceUsers = useSelector(state => state.Workspace?.members || []);

  // Get existing data from Redux store for preview
  const deals = useSelector(state => state.Objects?.deals?.records || []);
  const contacts = useSelector(state => state.Objects?.contacts?.records || []);
  const companies = useSelector(state => state.Objects?.companies?.records || []);
  const activities = useSelector(state => state.Activities?.allActivities || []);

  const [form, setForm] = useState(defaultWidgetConfig);
  const [isEditing, setIsEditing] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const titleInputRef = useRef(null);
  const titleManuallyEdited = useRef(false); // Track if user manually edited the title

  // Filter state for CRM categories
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterDefinition, setFilterDefinition] = useState({ filterGroups: [], groupOperator: 'OR' });

  // Table preview state
  const [tablePreviewData, setTablePreviewData] = useState([]);
  const [tablePreviewLoading, setTablePreviewLoading] = useState(false);

  // Flattened array of all filters for display (filter out undefined/null filters)
  const allFilters = filterDefinition?.filterGroups?.flatMap(g => (g.filters || []).filter(f => f).map(f => ({ ...f, groupId: g.id }))) || [];

  // Fetch pipelines and properties when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(getPipelines());

      // Fetch preview data for all categories (limit 3 records each)
      dispatch(getObjects({ objectType: 'deals', limit: 3, page: 1 }));
      dispatch(getObjects({ objectType: 'contacts', limit: 3, page: 1 }));
      dispatch(getObjects({ objectType: 'companies', limit: 3, page: 1 }));
      // Activities are already loaded in the Activities store
    }
  }, [isOpen, dispatch]);

  // Fetch properties when metricCategory changes (for filter support)
  useEffect(() => {
    if (isOpen && ['deals', 'contacts', 'companies', 'activities', 'agents'].includes(form.metricCategory)) {
      dispatch(getObjects({ objectType: form.metricCategory, limit: 1, page: 1 }));
    }
  }, [isOpen, form.metricCategory, dispatch]);

  // Cleanup when modal closes to prevent stale state issues
  useEffect(() => {
    if (!isOpen) {
      setFilterDefinition({ filterGroups: [], groupOperator: 'OR' });
      setShowFilterModal(false);
      titleManuallyEdited.current = false; // Reset manual edit flag
      setTablePreviewData([]);
    } else if (mode === 'add') {
      titleManuallyEdited.current = false; // Reset when opening in add mode
    }
  }, [isOpen, mode]);

  // Fetch filtered table preview data
  const fetchTablePreview = useCallback(() => {
    if (!isOpen || form.type !== 'table') return;

    const mongoFilters = filterDefinition?.filterGroups?.length > 0
      ? buildMongoQuery(filterDefinition.filterGroups, filterDefinition.groupOperator)
      : null;

    setTablePreviewLoading(true);

    // For all CRM objects (deals, contacts, companies, activities, agents), fetch from API
    if (['deals', 'contacts', 'companies', 'activities', 'agents'].includes(form.metricCategory)) {
      // For CRM objects, fetch from API
      dispatch(getObjects({
        objectType: form.metricCategory,
        filters: mongoFilters || {},
        limit: 10,
        page: 1,
        skipReduxUpdate: true,
        successCallback: (data) => {
          setTablePreviewData(data?.records || data || []);
          setTablePreviewLoading(false);
        }
      }));
    }
  }, [isOpen, form.type, form.metricCategory, filterDefinition, dispatch]);

  // Debounced fetch for table preview
  const fetchTimeoutRef = useRef(null);
  const prevFilterRef = useRef('');
  const prevCategoryRef = useRef('');
  const prevChartTypeRef = useRef('');

  useEffect(() => {
    if (!isOpen || form.type !== 'table') {
      // Reset refs when not table type
      if (form.type !== 'table') {
        prevChartTypeRef.current = form.type;
      }
      return;
    }

    const currentFilter = JSON.stringify(filterDefinition);
    const categoryChanged = form.metricCategory !== prevCategoryRef.current;
    const filterChanged = currentFilter !== prevFilterRef.current;
    const chartTypeChanged = form.type !== prevChartTypeRef.current;

    // Update refs
    prevFilterRef.current = currentFilter;
    prevCategoryRef.current = form.metricCategory;
    prevChartTypeRef.current = form.type;

    // Clear data and fetch if category changed
    if (categoryChanged || chartTypeChanged) {
      setTablePreviewData([]);
    }

    // Fetch if any relevant change occurred
    if (!categoryChanged && !filterChanged && !chartTypeChanged) return;

    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(fetchTablePreview, (categoryChanged || chartTypeChanged) ? 0 : 300);

    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, [isOpen, form.type, form.metricCategory, filterDefinition, fetchTablePreview]);

  const handleEditClick = () => {
    setIsTitleEditing(true);
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
  };

  const handleTitleBlur = () => {
    setIsTitleEditing(false);
    titleManuallyEdited.current = true; // Mark as manually edited
  };

  const handleTitleChange = (e) => {
    setForm({ ...form, title: e.target.value });
    titleManuallyEdited.current = true; // Mark as manually edited
  };

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && widgetData) {
        // Handle backward compatibility: convert old 'agent' to 'agents' and 'crm' to specific object type
        let metricCategory = widgetData.metricCategory || 'agents';
        if (metricCategory === 'agent') metricCategory = 'agents';
        if (metricCategory === 'crm') metricCategory = widgetData.crmObjectType || 'deals';

        // Convert legacy size to grid values if needed
        let gridCols = widgetData.gridCols;
        let gridRows = widgetData.gridRows || 2;
        if (!gridCols) {
          gridCols = widgetData.size === 'small' ? 4 : widgetData.size === 'medium' ? 8 : widgetData.size === 'large' ? 12 : 4;
        }

        setForm({
          type: widgetData.type || 'column',
          title: widgetData.title || '',
          metricCategory,
          // Agent fields
          agentId: widgetData.agentId || 'all',
          // CRM fields
          ownerId: widgetData.ownerId || 'all',
          pipelineId: widgetData.pipelineId || 'all',
          // Common fields
          dateRange: widgetData.dateRange || 'last_4_weeks',
          size: widgetData.size || 'small',
          viewBy: widgetData.viewBy || 'day',
          // Grid sizing
          gridCols,
          gridRows,
          // Number card color
          numberColor: widgetData.numberColor || null
        });
        setIsEditing(true);

        // Restore filter definition if exists (local state only - isolate mode)
        if (widgetData.filterDefinition && widgetData.filterDefinition.filterGroups) {
          // Ensure all filters in all groups are valid (not undefined/null)
          const sanitizedFilterDefinition = { ...widgetData.filterDefinition, filterGroups: widgetData.filterDefinition.filterGroups.map(group => ({ ...group, filters: (group.filters || []).filter(f => f && f.property && f.operator) })).filter(group => group.filters.length > 0)
          };
          setFilterDefinition(sanitizedFilterDefinition);
        } else setFilterDefinition({ filterGroups: [], groupOperator: 'OR' });
      } else {
        setForm(defaultWidgetConfig);
        setIsEditing(false);
        setFilterDefinition({ filterGroups: [], groupOperator: 'OR' });
      }
    }
  }, [isOpen, mode, widgetData, dispatch]);

  // Auto-update title when filters change (only for new widgets that haven't been manually edited)
  useEffect(() => {
    if (isOpen && mode === 'add' && !isTitleEditing && !titleManuallyEdited.current) {
      // Calculate allFilters inside useEffect to avoid dependency issues
      const currentFilters = filterDefinition?.filterGroups?.flatMap(g => (g.filters || []).filter(f => f).map(f => ({ ...f, groupId: g.id }))) || [];

      // Build title dynamically based on filters
      const categoryLabels = {
        agents: 'Agent',
        deals: 'Deal',
        contacts: 'Contact',
        companies: 'Company',
        activities: 'Activity'
      };
      const baseTitle = categoryLabels[form.metricCategory] || 'Widget';
      const filterParts = [];

      // Check for owner filter
      if (form.ownerId && form.ownerId !== 'all') filterParts.push('Owner');

      // Check for pipeline filter (deals only)
      if (form.metricCategory === 'deals' && form.pipelineId && form.pipelineId !== 'all') filterParts.push('Pipeline');

      // Check for applied filters from filterDefinition
      if (currentFilters && currentFilters.length > 0) {
        const filtersByProperty = {};
        currentFilters.forEach(filter => {
          const propName = filter.property?.name || 'Unknown';
          if (!filtersByProperty[propName]) filtersByProperty[propName] = [];
          filtersByProperty[propName].push(filter);
        });

        Object.keys(filtersByProperty).forEach(propName => {
          const propLabel = propName.charAt(0).toUpperCase() + propName.slice(1).replace(/_/g, ' ');
          if (propName === 'tags') filterParts.push('Tags');
         else filterParts.push(propLabel);
        });
      }

      // Build final title
      const newTitle = filterParts.length > 0 ? `${baseTitle} By ${filterParts.join(' ')}` : baseTitle;
      setForm(prev => ({ ...prev, title: newTitle }));
    }
  }, [filterDefinition, form.ownerId, form.pipelineId, form.metricCategory, mode, isOpen, isTitleEditing]);

  // Auto-generate title based on category and filters (e.g., "Agent By Tags", "Deal By Owner")
  const getDefaultTitle = () => {
    const categoryLabels = {
      agents: 'Agent',
      deals: 'Deal',
      contacts: 'Contact',
      companies: 'Company',
      activities: 'Activity'
    };

    const baseTitle = categoryLabels[form.metricCategory] || 'Widget';

    // Build filter description for title
    const filterParts = [];

    // Check for owner filter
    if (form.ownerId && form.ownerId !== 'all') {
      filterParts.push('Owner');
    }

    // Check for pipeline filter (deals only)
    if (form.metricCategory === 'deals' && form.pipelineId && form.pipelineId !== 'all') {
      filterParts.push('Pipeline');
    }

    // Check for applied filters from filterDefinition
    if (allFilters && allFilters.length > 0) {
      const filtersByProperty = {};
      allFilters.forEach(filter => {
        const propName = filter.property?.name || 'Unknown';
        if (!filtersByProperty[propName]) {
          filtersByProperty[propName] = [];
        }
        filtersByProperty[propName].push(filter);
      });

      Object.keys(filtersByProperty).forEach(propName => {
        const propLabel = propName.charAt(0).toUpperCase() + propName.slice(1).replace(/_/g, ' ');
        if (propName === 'tags') {
          filterParts.push('Tags');
        } else {
          filterParts.push(propLabel);
        }
      });
    }

    // Build final title
    if (filterParts.length > 0) {
      return `${baseTitle} By ${filterParts.join(' ')}`;
    }

    return baseTitle;
  };

  // Get subtitle based on category (just shows object type: "Agents", "Deals", etc.)
  const getSubtitle = () => {
    const category = metricCategories.find(t => t.value === form.metricCategory);
    return category?.label || form.metricCategory;
  };

  // For backward compatibility
  const getAgentSubtitle = getSubtitle;

  // Filter handlers for CRM categories (local state only - isolate mode)
  const handleRemoveFilter = useCallback((groupId, filterId) => {
    const updatedGroups = filterDefinition.filterGroups.map(group => {
      if (group.id === groupId) return { ...group, filters: group.filters.filter(f => f.id !== filterId) };
      return group;
    }).filter(group => group.filters && group.filters.length > 0);

    const updatedFilters = { ...filterDefinition, filterGroups: updatedGroups };
    setFilterDefinition(updatedFilters);
  }, [filterDefinition]);

  const handleClearFilters = useCallback(() => {
    const emptyFilters = { filterGroups: [], groupOperator: 'OR' };
    setFilterDefinition(emptyFilters);
  }, []);

  // Format filter display for chips
  const formatFilterChipLabel = (filter) => {
    // Guard against undefined/null filter
    if (!filter) return '';

    let displayValue = filter.value;
    if (Array.isArray(displayValue)) {
      displayValue = displayValue.join(', ');
    } else if (typeof displayValue === 'object' && displayValue !== null) {
      const trueKeys = Object.keys(displayValue).filter(key => displayValue[key]);
      displayValue = displayValue.label || displayValue.name || (trueKeys.length > 0 ? trueKeys.join(', ') : '');
    }

    // Guard against undefined property or operator
    const propertyLabel = filter.property?.label || filter.property?.name || filter.propertyName || 'Unknown';
    const operatorLabel = filter.operator?.label || filter.operatorSymbol || '';

    return `${propertyLabel} ${operatorLabel} ${displayValue || ''}`.trim();
  };

  const handleSave = () => {
    const title = form.title.trim() || getDefaultTitle();
    // Convert grid columns to legacy size for backward compatibility
    const legacySize = form.gridCols <= 4 ? 'small' : form.gridCols <= 8 ? 'medium' : 'large';

    const widgetConfig = {
      type: form.type,
      title,
      // All object types (deals, contacts, companies, activities, agents) use 'crm' metricCategory
      metricCategory: 'crm',
      dateRange: form.dateRange,
      size: legacySize,
      viewBy: form.viewBy,
      // Grid sizing
      gridCols: form.gridCols,
      gridRows: form.gridRows,
      id: isEditing && widgetData?.id ? widgetData.id : `widget-${Date.now()}`
    };

    // Add category-specific fields
    // Treat agents as CRM object with property-based filtering
    widgetConfig.crmObjectType = form.metricCategory; // deals, contacts, companies, activities, agents
    widgetConfig.ownerId = form.ownerId === 'all' ? null : form.ownerId;

    if (form.metricCategory === 'deals') {
      widgetConfig.pipelineId = form.pipelineId === 'all' ? null : form.pipelineId;
    }

    if (form.metricCategory === 'agents') {
      widgetConfig.agentId = form.agentId === 'all' ? null : form.agentId;
    }

    // Add filter definition for all CRM widgets including agents
    if (filterDefinition && filterDefinition.filterGroups && filterDefinition.filterGroups.length > 0) {
      widgetConfig.filterDefinition = filterDefinition;
      widgetConfig.filters = buildMongoQuery(filterDefinition.filterGroups, filterDefinition.groupOperator);
    } else {
      // Explicitly clear filters when they're empty
      widgetConfig.filterDefinition = null;
      widgetConfig.filters = null;
    }

    // Add number card color (if number type)
    if (form.type === 'number') {
      widgetConfig.numberColor = form.numberColor;
    }

    onSave(widgetConfig);
  };

  const getChartIcon = (type) => {
    switch (type) {
      case 'column':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="4" y="10" width="4" height="10"></rect>
            <rect x="10" y="6" width="4" height="14"></rect>
            <rect x="16" y="2" width="4" height="18"></rect>
          </svg>
        );
      case 'bar':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="4" y="4" width="10" height="4"></rect>
            <rect x="4" y="10" width="14" height="4"></rect>
            <rect x="4" y="16" width="8" height="4"></rect>
          </svg>
        );
      case 'donut':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <circle cx="12" cy="12" r="4"></circle>
          </svg>
        );
      case 'line':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="4 16 8 10 14 14 20 6"></polyline>
          </svg>
        );
      case 'number':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <text x="12" y="16" textAnchor="middle" fontSize="14" fontWeight="bold" fill="currentColor">123</text>
          </svg>
        );
      case 'table':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"></rect>
            <line x1="3" y1="9" x2="21" y2="9"></line>
            <line x1="3" y1="15" x2="21" y2="15"></line>
            <line x1="9" y1="3" x2="9" y2="21"></line>
          </svg>
        );
      default:
        return null;
    }
  };

  // Get preview categories based on viewBy selection
  const getPreviewCategories = (viewBy) => {
    switch (viewBy) {
      case 'hour':
        return ['10:00', '11:00', '12:00', '13:00', '14:00'];
      case 'week':
        return ['W1', 'W2', 'W3', 'W4', 'W5'];
      case 'month':
        return ['Jan', 'Feb', 'Mar', 'Apr', 'May'];
      case 'day':
      default:
        return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    }
  };

  // Helper function to get preview labels (at least 3 entries)
  const getPreviewLabels = (category, count = 3) => {
    switch (category) {
      case 'agents':
        return Array.from({ length: count }, (_, i) => agents[i]?.name || `Agent ${i + 1}`);
      case 'deals':
        return Array.from({ length: count }, (_, i) => deals[i]?.title || `Deal ${i + 1}`);
      case 'contacts':
        return Array.from({ length: count }, (_, i) =>
          contacts[i]?.firstName ? `${contacts[i].firstName} ${contacts[i].lastName || ''}`.trim() : `Contact ${i + 1}`
        );
      case 'companies':
        return Array.from({ length: count }, (_, i) => companies[i]?.name || `Company ${i + 1}`);
      case 'activities':
        return Array.from({ length: count }, (_, i) => activities[i]?.title || `Activity ${i + 1}`);
      default:
        return Array.from({ length: count }, (_, i) => `Item ${i + 1}`);
    }
  };

  // Generate preview chart config
  const previewConfig = useMemo(() => {
    if (!form.type) return null;

    const categories = getPreviewCategories(form.viewBy);

    switch (form.type) {
      case 'number':
        return { type: 'number', value: 157 };
      case 'donut':
        return {
          type: 'donut',
          options: {
            chart: { type: 'donut', animations: { enabled: false } },
            labels: ['Category A', 'Category B', 'Category C'],
            colors: constants.chartColors.slice(0, 3),
            legend: { position: 'bottom', fontSize: '11px' },
            dataLabels: { enabled: false }
          },
          series: [44, 35, 21]
        };
      case 'column':
      case 'bar':
        return {
          type: 'bar',
          options: {
            chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false } },
            plotOptions: { bar: { horizontal: form.type === 'bar', borderRadius: 4, distributed: true } },
            colors: constants.chartColors,
            xaxis: { categories },
            dataLabels: { enabled: false },
            legend: { show: false }
          },
          series: [{ name: 'Data', data: [30, 40, 45, 50, 35] }]
        };
      case 'line':
        const previewLineColors = [constants.chartColors[0], constants.chartColors[1]];
        return {
          type: 'area',
          options: {
            chart: { type: 'area', toolbar: { show: false }, animations: { enabled: false } },
            colors: previewLineColors,
            fill: {
              type: 'gradient',
              gradient: {
                shade: 'light',
                type: 'vertical',
                shadeIntensity: 0.3,
                gradientToColors: ['#ffffff', '#ffffff'],
                inverseColors: false,
                opacityFrom: 0.5,
                opacityTo: 0.1,
                stops: [0, 100]
              }
            },
            stroke: { curve: 'smooth', width: 2.5, lineCap: 'round' },
            xaxis: {
              categories,
              axisBorder: { show: false },
              axisTicks: { show: false },
              labels: { style: { colors: '#94a3b8', fontWeight: 400 } }
            },
            yaxis: {
              labels: { style: { colors: '#94a3b8', fontWeight: 400 } },
              min: 0
            },
            grid: {
              borderColor: '#f1f5f9',
              xaxis: { lines: { show: false } },
              yaxis: { lines: { show: true } }
            },
            markers: {
              size: 5,
              colors: ['#ffffff'],
              strokeColors: previewLineColors,
              strokeWidth: 2.5,
              strokeOpacity: 1,
              fillOpacity: 1,
              shape: 'circle',
              hover: { size: 7 }
            },
            legend: {
              show: true,
              position: 'bottom',
              horizontalAlign: 'center',
              fontSize: '11px',
              fontWeight: 500,
              markers: { width: 24, height: 4, radius: 2, offsetX: -4 },
              itemMargin: { horizontal: 15, vertical: 5 }
            },
            dataLabels: { enabled: false }
          },
          series: (() => {
            const labels = getPreviewLabels(form.metricCategory, 3);
            return [
              { name: labels[0], data: [10, 25, 15, 30, 20, 25, 18] },
              { name: labels[1], data: [15, 18, 28, 12, 22, 30, 25] },
              { name: labels[2], data: [20, 15, 22, 18, 25, 20, 23] }
            ];
          })()
        };
      case 'table':
        // Use filtered data if available, otherwise placeholder
        const hasFilteredData = tablePreviewData && tablePreviewData.length > 0;
        const tableData = hasFilteredData
          ? tablePreviewData.map(item => {
              // Dynamic label based on object type
              const label = form.metricCategory === 'agents' ? (item.name || 'Agent')
                : form.metricCategory === 'deals' ? (item.title || item.name || 'Deal')
                : form.metricCategory === 'contacts' ? (item.firstName ? `${item.firstName} ${item.lastName || ''}`.trim() : item.email || 'Contact')
                : form.metricCategory === 'companies' ? (item.name || 'Company')
                : form.metricCategory === 'activities' ? (item.title || item.subject || 'Activity')
                : (item.name || item.title || 'Item');
              return {
                label,
                value: item.amount || item.value || '-',
                status: item.status || item.stage || item.type || '-'
              };
            })
          : (() => {
              const labels = getPreviewLabels(form.metricCategory, 4);
              return [
                { label: labels[0], value: 324, change: '+12%' },
                { label: labels[1], value: 256, change: '+8%' },
                { label: labels[2], value: 189, change: '-3%' },
                { label: labels[3], value: 145, change: '+5%' }
              ];
            })();
        return { type: 'table', data: tableData, isFiltered: hasFilteredData };
      default:
        return null;
    }
  }, [form.type, form.viewBy, form.metricCategory, agents, deals, contacts, companies, activities, tablePreviewData]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="chart-modal" size="lg" backdrop="static">
      <ModalHeader toggle={onClose}>
        {mode === 'edit' ? 'Edit Widget' : 'Add a Custom Chart'}
      </ModalHeader>
      <ModalBody>
        <div className="chart-modal-content">
          <div className="form-column">
            {/* Chart Type */}
            <div className="form-section">
              <label className="form-label">Graph Type</label>
              <div className="chart-type-grid">
                {chartTypes.map(type => (
                  <button
                    key={type.value}
                    className={`chart-type-btn ${form.type === type.value ? 'selected' : ''}`}
                    onClick={() => setForm({ ...form, type: type.value })}
                  >
                    {getChartIcon(type.value)}
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="form-section">
              <label className="form-label">Date Range</label>
              <FormControl fullWidth size="small">
                <Select
                  value={form.dateRange}
                  onChange={(e) => setForm({ ...form, dateRange: e.target.value })}
                >
                  {dateRangeOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <p className="form-hint">The date range filter will override this charts date range.</p>
            </div>

            {/* Metric Category Dropdown */}
            <div className="form-section">
              <label className="form-label">Object Type</label>
              <FormControl fullWidth size="small">
                <Select
                  value={form.metricCategory}
                  onChange={(e) => {
                    const newCategory = e.target.value;
                    const categoryLabels = {
                      agents: 'Agents',
                      deals: 'Deals',
                      contacts: 'Contacts',
                      companies: 'Companies',
                      activities: 'Activities'
                    };
                    const defaultTitle = categoryLabels[newCategory] || 'New Widget';
                    setForm({ ...form, metricCategory: newCategory, title: defaultTitle });
                  }}
                  renderValue={(value) => {
                    const cat = metricCategories.find(c => c.value === value);
                    return (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {cat?.icon}
                        {cat?.label}
                      </span>
                    );
                  }}
                >
                  {metricCategories.map(cat => (
                    <MenuItem key={cat.value} value={cat.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {cat.icon}
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Agent Selection */}
            {form.metricCategory === 'agents' && (
              <div className="form-section">
                <label className="form-label">Agent</label>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.agentId}
                    onChange={(e) => setForm({ ...form, agentId: e.target.value })}
                  >
                    <MenuItem value="all">All agents</MenuItem>
                    {agents.map(agent => (
                      <MenuItem key={agent._id} value={agent._id}>{agent.name || 'Unnamed Agent'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}

            {/* Pipeline filter for deals */}
            {form.metricCategory === 'deals' && (
              <div className="form-section">
                <label className="form-label">Pipeline</label>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.pipelineId}
                    onChange={(e) => setForm({ ...form, pipelineId: e.target.value })}
                  >
                    <MenuItem value="all">All Pipelines</MenuItem>
                    {pipelines.map(pipeline => (
                      <MenuItem key={pipeline._id} value={pipeline._id}>{pipeline.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}


            {/* View By - Only show in add mode */}
            {mode !== 'edit' && (
              <div className="form-section">
                <label className="form-label">View By</label>
                <div className="view-by-options">
                  {viewByOptions.map(option => (
                    <button
                      key={option.value}
                      className={`view-by-btn ${form.viewBy === option.value ? 'selected' : ''}`}
                      onClick={() => setForm({ ...form, viewBy: option.value })}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>

          <div className="preview-column">
            <div className="preview-header">
              <span>Chart</span>
              {/* Quick Filters - For all object types with properties */}
              {['deals', 'contacts', 'companies', 'activities', 'agents'].includes(form.metricCategory) && (
                <div style={{ marginLeft: '16px', flex: 1, overflow: 'hidden' }}>
                  <QuickFilters
                      objectType={form.metricCategory}
                      config={
                        form.metricCategory === 'deals' ? dashboardDealsQuickFilterConfig :
                        form.metricCategory === 'contacts' ? contactsQuickFilterConfig :
                        form.metricCategory === 'companies' ? companiesQuickFilterConfig :
                        form.metricCategory === 'activities' ? activitiesQuickFilterConfig :
                        agentsQuickFilterConfig
                      }
                      onAdvancedClick={() => setShowFilterModal(true)}
                      isolate={true}
                      initialFilters={filterDefinition}
                      onGetData={(data) => {
                        // Update local filter state from QuickFilters (isolate mode returns filterDefinition)
                        // Only update if the filter actually changed to prevent infinite loops
                        if (data.filterDefinition && JSON.stringify(data.filterDefinition) !== JSON.stringify(filterDefinition)) {
                          setFilterDefinition(data.filterDefinition);
                        }
                      }}
                    />
                </div>
              )}
            </div>

            {/* Active Filter Chips - For all object types with properties */}
            {['deals', 'contacts', 'companies', 'activities', 'agents'].includes(form.metricCategory) && allFilters.length > 0 && (
              <div className="active-filters-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px 0', marginBottom: '8px' }}>
                {allFilters.map(filter => (
                  <Chip
                    size="small"
                    key={filter.id}
                    variant="outlined"
                    label={formatFilterChipLabel(filter)}
                    onDelete={() => handleRemoveFilter(filter.groupId, filter.id)}
                    sx={{ fontSize: '11px' }}
                  />
                ))}
                <Button size="small" onClick={handleClearFilters} sx={{ fontSize: '11px', textTransform: 'none' }}>Clear All</Button>
              </div>
            )}

            <div className="preview-content-wrapper">
              <div
                className={`preview-card${form.type === 'number' ? ' number-card-preview' : ''}`}
                style={{
                  '--preview-cols': form.gridCols,
                  '--preview-rows': form.gridRows,
                  ...(form.type === 'number' && { '--number-bg-color': form.numberColor || constants.chartColors[0] })
                }}
              >
                <div className="preview-card-header">
                  <div className={`preview-title-input ${isTitleEditing ? 'editing' : ''}`}>
                    {isTitleEditing ? (
                      <input
                        ref={titleInputRef}
                        type="text"
                        className="title-editable-input active"
                        value={form.title}
                        onChange={handleTitleChange}
                        placeholder={getDefaultTitle()}
                        onBlur={handleTitleBlur}
                        onKeyDown={(e) => e.key === 'Enter' && handleTitleBlur()}
                      />
                    ) : (
                      <>
                        <span className="title-text">{form.title || getDefaultTitle()}</span>
                        <svg
                          className="edit-icon"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          onClick={handleEditClick}
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </>
                    )}
                  </div>
                  <span className="preview-subtitle">
                    {getAgentSubtitle()}
                  </span>
                </div>
                <div className="preview-chart">
                  {previewConfig?.type === 'number' ? (
                    <div className="preview-number">{previewConfig.value}</div>
                  ) : previewConfig?.type === 'table' ? (
                    <div className={`preview-table ${form.gridCols <= 4 ? 'compact' : form.gridCols <= 6 ? 'medium' : 'large'}`}>
                      {tablePreviewLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                          <CircularProgress size={24} />
                        </div>
                      ) : previewConfig.data.length === 0 ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px', color: '#666' }}>
                          No data found
                        </div>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>{previewConfig.isFiltered ? 'Value' : 'Value'}</th>
                              <th>{previewConfig.isFiltered ? 'Status' : 'Change'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewConfig.data.slice(0, form.gridRows <= 2 ? 3 : 4).map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.label}</td>
                                <td>{row.value}</td>
                                <td className={!previewConfig.isFiltered && row.change?.startsWith?.('+') ? 'positive' : !previewConfig.isFiltered && row.change?.startsWith?.('-') ? 'negative' : ''}>
                                  {previewConfig.isFiltered ? row.status : row.change}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ) : previewConfig && previewConfig.options && previewConfig.series ? (
                    <Apexcharts
                      key={form.type}
                      options={previewConfig.options}
                      series={previewConfig.series}
                      type={previewConfig.type}
                      height={160}
                    />
                  ) : null}
                </div>
              </div>

              {/* Grid Size Selector */}
              <div className="grid-size-selector">
                <div className="grid-size-section">
                  <label className="grid-size-label">Width</label>
                  <div className="grid-size-options">
                    {gridColOptions.map(opt => (
                      <button
                        key={opt.value}
                        className={`grid-size-btn ${form.gridCols === opt.value ? 'selected' : ''}`}
                        onClick={() => setForm({ ...form, gridCols: opt.value })}
                      >
                        {opt.value}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid-size-section">
                  <label className="grid-size-label">Height</label>
                  <div className="grid-size-options">
                    {gridRowOptions.map(opt => (
                      <button
                        key={opt.value}
                        className={`grid-size-btn ${form.gridRows === opt.value ? 'selected' : ''}`}
                        onClick={() => setForm({ ...form, gridRows: opt.value })}
                      >
                        {opt.value}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Color Picker - Only show for Number chart type */}
              {form.type === 'number' && (
                <div className="color-picker-section">
                  <label className="grid-size-label">Background Color</label>
                  <div className="color-picker-grid">
                    <button
                      className={`color-option ${form.numberColor === null ? 'selected' : ''}`}
                      onClick={() => setForm({ ...form, numberColor: null })}
                      title="Random"
                    >
                      <span className="random-color-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                        </svg>
                      </span>
                    </button>
                    {constants.chartColors.map((color, idx) => (
                      <button
                        key={idx}
                        className={`color-option ${form.numberColor === color ? 'selected' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setForm({ ...form, numberColor: color })}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn-style-border" onClick={onClose}>Cancel</button>
        <button className="btn-style-new" onClick={handleSave}>Save</button>
      </ModalFooter>

      {/* Advanced Filter Modal - For all object types with properties (deals, contacts, companies, activities, agents) */}
      {['deals', 'contacts', 'companies', 'activities', 'agents'].includes(form.metricCategory) && (
        <AdvancedFilterModal
          objectType={form.metricCategory}
          isOpen={showFilterModal}
          isolate={true}
          initialFilters={filterDefinition}
          onClose={() => {
            setShowFilterModal(false);
          }}
          onGetData={(data) => {
            // Update local filter state from AdvancedFilterModal (isolate mode returns filterDefinition)
            if (data.filterDefinition) {
              setFilterDefinition(data.filterDefinition);
            }
          }}
        />
      )}
    </Modal>
  );
};

export default ChartModal;
